import { Stack } from '@mui/material'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCanAccessRecordPageInList } from '@omnea-app/fe-authz/useRouteAccess'
import { CellActionButton } from '@omnea-app/fe-tables/columns/definitions/helpers/CellActionButton'
import { ProfileBox } from '@omnea-app/fe-users-and-groups/ProfileBox'
import { ShareOMIcon } from '@omnea-app/icons'
import { generateSupplierRoute } from '../supplier-details-page/generateSupplierRoute'

type Props = {
  supplierId?: string | null
  name: string
  logoUrl?: string | null
  // The grid cell's own focus, so keyboard users reach the button by moving onto the cell.
  hasFocus?: boolean
  tabIndex?: 0 | -1
}

export const SupplierLink = ({ supplierId, name, logoUrl, hasFocus = false, tabIndex }: Props) => {
  const { push } = useRouter()
  const { t } = useTranslation('shared')
  const [isHovered, setIsHovered] = useState(false)
  const canOpenSupplier = useCanAccessRecordPageInList('supplier', supplierId ?? undefined) && supplierId != null

  const openSupplier = () => {
    if (canOpenSupplier) void push(generateSupplierRoute({ supplierId, tab: 'overview' }))
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      gap={1}
      px={1}
      width="100%"
      height="100%"
      minWidth={0}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <ProfileBox
        userId={null}
        text={name}
        imgUrl={logoUrl}
        disableTooltip
        avatarVariant="square"
        onClick={
          canOpenSupplier
            ? (event) => {
                // Rows are often links themselves; the name opens the supplier instead of the row.
                event.preventDefault()
                event.stopPropagation()
                openSupplier()
              }
            : undefined
        }
      />
      {canOpenSupplier && (isHovered || hasFocus) && (
        <CellActionButton label={t('table.open', { name })} Icon={ShareOMIcon} tabIndex={tabIndex} onClick={openSupplier} />
      )}
    </Stack>
  )
}
